import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import PhoneInput from '../components/common/PhoneInput';

export default function SuperAdminOrganismes() {
  const [organismes, setOrganismes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOrganisme, setEditingOrganisme] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    acronyme: '',
    typeOrganisme: '',
    adresse: {
      rue: '',
      codePostal: '',
      ville: ''
    },
    telephone: '',
    email: '',
    siteWeb: '',
    description: '',
    siret: ''
  });

  useEffect(() => {
    fetchOrganismes();
  }, []);

  const fetchOrganismes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/organismes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrganismes(response.data);
    } catch (error) {
      alert('Erreur lors du chargement des organismes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingOrganisme) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/organismes/${editingOrganisme._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Organisme mis à jour avec succès');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/organismes`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Organisme créé avec succès');
      }
      
      setShowModal(false);
      resetForm();
      fetchOrganismes();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (organisme) => {
    setEditingOrganisme(organisme);
    setFormData(organisme);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet organisme ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/organismes/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Organisme supprimé avec succès');
      fetchOrganismes();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingOrganisme(null);
    setFormData({
      nom: '',
      acronyme: '',
      typeOrganisme: '',
      adresse: { rue: '', codePostal: '', ville: '' },
      telephone: '',
      email: '',
      siteWeb: '',
      description: '',
      siret: ''
    });
  };

  if (loading) {
    return <div className="p-8">Chargement...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Organismes</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nouvel Organisme
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {organismes.map((organisme) => (
          <div key={organisme._id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <BuildingOfficeIcon className="h-10 w-10 text-indigo-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{organisme.nom}</h3>
                  <p className="text-sm text-gray-500">{organisme.acronyme}</p>
                  <p className="text-xs text-gray-400 capitalize">{organisme.typeOrganisme}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                organisme.actif ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {organisme.actif ? 'Actif' : 'Inactif'}
              </span>
            </div>

            <div className="space-y-2 text-sm text-gray-600 mb-4">
              <p>📍 {organisme.adresse.ville}</p>
              <p>📧 {organisme.email}</p>
              <p>📞 {organisme.telephone}</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(organisme)}
                className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Modifier
              </button>
              <button
                onClick={() => handleDelete(organisme._id)}
                className="flex-1 flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold mb-6">
              {editingOrganisme ? 'Modifier l\'organisme' : 'Nouvel organisme'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Acronyme *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.acronyme}
                    onChange={(e) => setFormData({ ...formData, acronyme: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type d'organisme *
                </label>
                <select
                  required
                  value={formData.typeOrganisme}
                  onChange={(e) => setFormData({ ...formData, typeOrganisme: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Sélectionner un type</option>
                  <option value="association">Association</option>
                  <option value="syndicat">Syndicat</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rue *
                </label>
                <input
                  type="text"
                  required
                  value={formData.adresse.rue}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    adresse: { ...formData.adresse, rue: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code Postal *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.adresse.codePostal}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      adresse: { ...formData.adresse, codePostal: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.adresse.ville}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      adresse: { ...formData.adresse, ville: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone *
                  </label>
                  <PhoneInput
                    name="telephone"
                    id="organismePhone"
                    value={formData.telephone || ''}
                    onChange={(value) => setFormData({ ...formData, telephone: value })}
                    placeholder=""
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Web
                  </label>
                  <input
                    type="url"
                    value={formData.siteWeb}
                    onChange={(e) => setFormData({ ...formData, siteWeb: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SIRET
                  </label>
                  <input
                    type="text"
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  {editingOrganisme ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
